/**
 * Sites v2 — data layer for the creator website templates (T1–T7)
 * and Orbit Admin. Pairs with supabase/migrations/20260905100000_sites_v2.sql.
 *
 *   Draft   — `studio_sites.site_template` + `site_config`, plus the
 *             content tables (site_projects / site_services /
 *             site_testimonials / site_posts). Edited under RLS.
 *   Live    — `publish_site_v2` compiles ONE snapshot; visitors read
 *             it through `get_public_site_v2` / `…_by_host`.
 *   Inbox   — `site_inquiries` (+ messages), `site_newsletter_subscribers`.
 *   Numbers — `site_v2_analytics` rollup over `site_analytics_events`.
 *
 * The generated Supabase types don't know the v2 tables until Lovable
 * regenerates them, so table access goes through a loosely-typed
 * handle (`db`) with explicit row types on every result.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useOrganization } from '@/services/organizations';
import { useAuth } from '@/hooks/useAuth';
import { useStudioSiteMedia, type StudioSiteMedia } from '@/services/siteMedia';
import {
  parseSnapshot,
  type SiteConfig,
  type SiteMediaRef,
  type SitePostPublic,
  type SiteProjectPublic,
  type SiteServicePublic,
  type SiteSnapshot,
  type SiteTemplateId,
  type SiteTestimonialPublic,
} from '@/components/sites/v2/types';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = supabase as unknown as { from: (table: string) => any; rpc: (fn: string, args?: Record<string, unknown>) => any };

export const SITES_V2_KEY = 'sites-v2';

// ─────────────────────────────────────────────────────────────────────
// Row types
// ─────────────────────────────────────────────────────────────────────

export interface StudioSiteV2 {
  id: string;
  org_id: string;
  slug: string;
  site_template: SiteTemplateId;
  site_config: SiteConfig;
  published_snapshot: SiteSnapshot | null;
  published_at: string | null;
  unpublished_at: string | null;
  draft_updated_at: string;
  custom_domain_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface SiteProjectRow {
  id: string;
  org_id: string;
  site_id: string;
  slug: string;
  title: string;
  category: string | null;
  client: string | null;
  year: string | null;
  location: string | null;
  role: string | null;
  intro: string | null;
  body: string | null;
  stats: Array<[string, string]>;
  cover_media_id: string | null;
  status: 'draft' | 'published';
  featured: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface SiteProjectMediaRow {
  id: string;
  org_id: string;
  site_id: string;
  project_id: string;
  media_id: string;
  caption: string | null;
  sort_order: number;
  created_at: string;
}

export interface SiteServiceRow {
  id: string;
  org_id: string;
  site_id: string;
  name: string;
  price: string | null;
  duration: string | null;
  includes: string[];
  blurb: string | null;
  visible: boolean;
  featured: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface SiteTestimonialRow {
  id: string;
  org_id: string;
  site_id: string;
  quote: string;
  author: string | null;
  role: string | null;
  visible: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface SitePostRow {
  id: string;
  org_id: string;
  site_id: string;
  slug: string;
  title: string;
  excerpt: string | null;
  body: string | null;
  tags: string[];
  cover_media_id: string | null;
  status: 'draft' | 'published' | 'scheduled';
  published_at: string | null;
  read_minutes: number | null;
  created_at: string;
  updated_at: string;
}

export type SiteInquiryStatus = 'new' | 'replied' | 'booked' | 'archived';

export interface SiteInquiryRow {
  id: string;
  org_id: string;
  site_id: string;
  name: string;
  email: string;
  phone: string | null;
  type: string | null;
  event_date: string | null;
  budget: string | null;
  message: string | null;
  status: SiteInquiryStatus;
  source: string;
  referrer: string | null;
  inquiry_id: string | null;
  replied_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface SiteInquiryMessageRow {
  id: string;
  org_id: string;
  inquiry_id: string;
  direction: 'in' | 'out' | 'note';
  body: string;
  created_by: string | null;
  created_at: string;
}

export interface SitePublishHistoryRow {
  id: string;
  site_id: string;
  snapshot_summary: Record<string, unknown>;
  published_by: string | null;
  published_by_name: string | null;
  published_at: string;
  label: string | null;
  notes: string | null;
  restored_from_id: string | null;
  created_at: string;
}

export interface SiteAnalytics {
  from: string;
  days: number;
  totals: { visitors: number; pageviews: number; inquiries: number; cta_clicks: number; subscribes: number };
  previous: { visitors: number; pageviews: number; inquiries: number };
  daily: Array<{ day: string; visitors: number; pageviews: number; inquiries: number }>;
  top_pages: Array<{ page: string; views: number }>;
  sources: Array<{ source: string; visitors: number }>;
  devices: Array<{ device: string; visitors: number }>;
  inquiries_by_type: Array<{ type: string; count: number }>;
  top_projects: Array<{ project_id: string | null; title: string | null; views: number }>;
}

// ─────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────

export const slugifyText = (s: string): string =>
  s
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')
    .slice(0, 80) || 'untitled';

/** Same liveness rule as the SQL (`site_v2_is_live`). */
export function isSiteV2Live(site: Pick<StudioSiteV2, 'published_at' | 'unpublished_at' | 'published_snapshot'> | null | undefined): boolean {
  if (!site?.published_at) return false;
  if (site.unpublished_at && site.unpublished_at >= site.published_at) return false;
  return !!site.published_snapshot;
}

export function hasUnpublishedChanges(site: Pick<StudioSiteV2, 'published_at' | 'draft_updated_at'> | null | undefined): boolean {
  if (!site) return false;
  if (!site.published_at) return true;
  return new Date(site.draft_updated_at).getTime() > new Date(site.published_at).getTime() + 1000;
}

/** Public URL of the site on Orbit (the free address). */
export function siteV2PublicPath(slug: string): string {
  return `/s/${slug}`;
}
export function siteV2PublicUrl(slug: string): string {
  if (typeof window === 'undefined') return siteV2PublicPath(slug);
  return `${window.location.origin}${siteV2PublicPath(slug)}`;
}

/** Library row → public media reference (what the snapshot carries). */
export function mediaRefFromRow(m: StudioSiteMedia | null | undefined, caption?: string | null): SiteMediaRef | null {
  if (!m) return null;
  return {
    id: m.id,
    kind: m.kind,
    url: m.public_url,
    playback_id: m.mux_playback_id,
    width: m.width,
    height: m.height,
    alt: m.alt_text,
    duration_ms: m.duration_ms,
    caption: caption ?? null,
  };
}

const rowsOf = <T,>(res: { data: unknown; error: { message: string } | null }): T[] => {
  if (res.error) throw new Error(res.error.message);
  return (res.data as T[] | null) ?? [];
};
const rowOf = <T,>(res: { data: unknown; error: { message: string } | null }): T => {
  if (res.error) throw new Error(res.error.message);
  return res.data as T;
};

// ─────────────────────────────────────────────────────────────────────
// The site row (one per org; created on first visit)
// ─────────────────────────────────────────────────────────────────────

const SITE_COLUMNS =
  'id, org_id, slug, site_template, site_config, published_snapshot, published_at, unpublished_at, draft_updated_at, custom_domain_id, created_at, updated_at';

export function useSiteV2() {
  const { data: org } = useOrganization();
  const orgId = org?.id;
  return useQuery({
    queryKey: [SITES_V2_KEY, 'site', orgId],
    enabled: !!orgId,
    queryFn: async (): Promise<StudioSiteV2 | null> => {
      if (!orgId) return null;
      const existing = await db.from('studio_sites').select(SITE_COLUMNS).eq('org_id', orgId).maybeSingle();
      if (existing.error) throw new Error(existing.error.message);
      if (existing.data) return existing.data as StudioSiteV2;

      const seed = `${slugifyText(org?.name ?? 'studio')}-${orgId.slice(0, 6)}`;
      const created = await db
        .from('studio_sites')
        .insert({
          org_id: orgId,
          slug: seed,
          site_template: 't1',
          site_config: { version: 2, brandName: org?.name ?? undefined },
          primary_color: (org as { brand_color?: string | null } | undefined)?.brand_color ?? null,
        })
        .select(SITE_COLUMNS)
        .single();
      const row = rowOf<StudioSiteV2 | null>(created);
      // AUDIT-1b — an insert that comes back empty (blocked by a policy,
      // a proxy, anything) used to leave the page on "Loading your
      // site…" forever, because null is neither data nor error.
      if (!row) throw new Error('Orbit couldn’t create the site row for your studio.');
      return row;
    },
  });
}

/**
 * Read-only lookup of the org's site row — for surfaces that only ask
 * "is the site live?" (activation progress, milestones) and must not
 * create a row as a side effect the way `useSiteV2` does.
 */
export function useSiteRowV2() {
  const { data: org } = useOrganization();
  const orgId = org?.id;
  return useQuery({
    queryKey: [SITES_V2_KEY, 'site-row', orgId],
    enabled: !!orgId,
    staleTime: 60_000,
    queryFn: async (): Promise<StudioSiteV2 | null> => {
      if (!orgId) return null;
      const res = await db.from('studio_sites').select(SITE_COLUMNS).eq('org_id', orgId).maybeSingle();
      if (res.error) throw new Error(res.error.message);
      return (res.data as StudioSiteV2 | null) ?? null;
    },
  });
}

export interface SiteV2Patch {
  site_config?: SiteConfig;
  site_template?: SiteTemplateId;
  slug?: string;
  custom_domain_id?: string | null;
}

export function useUpdateSiteV2() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; patch: SiteV2Patch }): Promise<StudioSiteV2> => {
      const res = await db.from('studio_sites').update(input.patch).eq('id', input.id).select(SITE_COLUMNS).single();
      return rowOf<StudioSiteV2>(res);
    },
    onSuccess: (row) => {
      qc.setQueryData([SITES_V2_KEY, 'site', row.org_id], row);
    },
  });
}

export function usePublishSiteV2() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { siteId: string; label?: string | null; notes?: string | null }) => {
      const res = await db.rpc('publish_site_v2', { p_site_id: input.siteId, p_label: input.label ?? null, p_notes: input.notes ?? null });
      if (res.error) throw new Error(res.error.message);
      return res.data as { history_id: string; published_at: string };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [SITES_V2_KEY] });
      qc.invalidateQueries({ queryKey: ['public-site-v2'] });
    },
  });
}

export function useUnpublishSiteV2() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string }): Promise<StudioSiteV2> => {
      const res = await db.from('studio_sites').update({ unpublished_at: new Date().toISOString() }).eq('id', input.id).select(SITE_COLUMNS).single();
      return rowOf<StudioSiteV2>(res);
    },
    onSuccess: (row) => {
      qc.setQueryData([SITES_V2_KEY, 'site', row.org_id], row);
      qc.invalidateQueries({ queryKey: ['public-site-v2'] });
    },
  });
}

export function useSiteHistoryV2(siteId: string | undefined) {
  return useQuery({
    queryKey: [SITES_V2_KEY, 'history', siteId],
    enabled: !!siteId,
    queryFn: async (): Promise<SitePublishHistoryRow[]> => {
      const res = await db
        .from('site_publish_history')
        .select('id, site_id, snapshot_summary, published_by, published_by_name, published_at, label, notes, restored_from_id, created_at')
        .eq('site_id', siteId)
        .order('published_at', { ascending: false })
        .limit(50);
      return rowsOf<SitePublishHistoryRow>(res);
    },
  });
}

export function useRestoreSiteV2() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { historyId: string }) => {
      const res = await db.rpc('restore_site_v2', { p_history_id: input.historyId });
      if (res.error) throw new Error(res.error.message);
      return res.data as { history_id: string; restored_from_id: string };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [SITES_V2_KEY] });
      qc.invalidateQueries({ queryKey: ['public-site-v2'] });
    },
  });
}

// ─────────────────────────────────────────────────────────────────────
// Generic content-table hooks
// ─────────────────────────────────────────────────────────────────────

type ContentTable = 'site_projects' | 'site_services' | 'site_testimonials' | 'site_posts';

function useContentList<T>(table: ContentTable, siteId: string | undefined, order: string) {
  return useQuery({
    queryKey: [SITES_V2_KEY, table, siteId],
    enabled: !!siteId,
    queryFn: async (): Promise<T[]> => {
      const res = await db.from(table).select('*').eq('site_id', siteId).order(order, { ascending: true }).order('created_at', { ascending: true });
      return rowsOf<T>(res);
    },
  });
}

function useContentUpsert<T extends { id: string; site_id: string }>(table: ContentTable) {
  const qc = useQueryClient();
  const { data: org } = useOrganization();
  return useMutation({
    mutationFn: async (input: { id?: string; site_id: string; values: Partial<T> }): Promise<T> => {
      if (input.id) {
        const res = await db.from(table).update(input.values).eq('id', input.id).select('*').single();
        return rowOf<T>(res);
      }
      if (!org?.id) throw new Error('Your studio isn’t ready yet.');
      const res = await db.from(table).insert({ ...input.values, org_id: org.id, site_id: input.site_id }).select('*').single();
      return rowOf<T>(res);
    },
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: [SITES_V2_KEY, table, row.site_id] });
      qc.invalidateQueries({ queryKey: [SITES_V2_KEY, 'site'] });
    },
  });
}

function useContentDelete(table: ContentTable) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; site_id: string }) => {
      const res = await db.from(table).delete().eq('id', input.id);
      if (res.error) throw new Error(res.error.message);
      return input;
    },
    onSuccess: (input) => {
      qc.invalidateQueries({ queryKey: [SITES_V2_KEY, table, input.site_id] });
      qc.invalidateQueries({ queryKey: [SITES_V2_KEY, 'site'] });
    },
  });
}

function useContentReorder(table: ContentTable) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { site_id: string; ids: string[] }) => {
      await Promise.all(
        input.ids.map(async (id, i) => {
          const res = await db.from(table).update({ sort_order: i }).eq('id', id);
          if (res.error) throw new Error(res.error.message);
        }),
      );
      return input;
    },
    onSuccess: (input) => {
      qc.invalidateQueries({ queryKey: [SITES_V2_KEY, table, input.site_id] });
    },
  });
}

// Projects
export const useSiteProjects = (siteId: string | undefined) => useContentList<SiteProjectRow>('site_projects', siteId, 'sort_order');
export const useUpsertSiteProject = () => useContentUpsert<SiteProjectRow>('site_projects');
export const useDeleteSiteProject = () => useContentDelete('site_projects');
export const useReorderSiteProjects = () => useContentReorder('site_projects');

export function useSiteProjectMedia(siteId: string | undefined) {
  return useQuery({
    queryKey: [SITES_V2_KEY, 'site_project_media', siteId],
    enabled: !!siteId,
    queryFn: async (): Promise<SiteProjectMediaRow[]> => {
      const res = await db.from('site_project_media').select('*').eq('site_id', siteId).order('sort_order', { ascending: true });
      return rowsOf<SiteProjectMediaRow>(res);
    },
  });
}

/** Replace a project's media list (ordered library ids). */
export function useSetSiteProjectMedia() {
  const qc = useQueryClient();
  const { data: org } = useOrganization();
  return useMutation({
    mutationFn: async (input: { site_id: string; project_id: string; media_ids: string[] }) => {
      if (!org?.id) throw new Error('Your studio isn’t ready yet.');
      const del = await db.from('site_project_media').delete().eq('project_id', input.project_id);
      if (del.error) throw new Error(del.error.message);
      if (input.media_ids.length) {
        const ins = await db.from('site_project_media').insert(
          input.media_ids.map((media_id, i) => ({ org_id: org.id, site_id: input.site_id, project_id: input.project_id, media_id, sort_order: i })),
        );
        if (ins.error) throw new Error(ins.error.message);
      }
      return input;
    },
    onSuccess: (input) => {
      qc.invalidateQueries({ queryKey: [SITES_V2_KEY, 'site_project_media', input.site_id] });
      qc.invalidateQueries({ queryKey: [SITES_V2_KEY, 'site'] });
    },
  });
}

// Services / testimonials / posts
export const useSiteServices = (siteId: string | undefined) => useContentList<SiteServiceRow>('site_services', siteId, 'sort_order');
export const useUpsertSiteService = () => useContentUpsert<SiteServiceRow>('site_services');
export const useDeleteSiteService = () => useContentDelete('site_services');
export const useReorderSiteServices = () => useContentReorder('site_services');

export const useSiteTestimonials = (siteId: string | undefined) => useContentList<SiteTestimonialRow>('site_testimonials', siteId, 'sort_order');
export const useUpsertSiteTestimonial = () => useContentUpsert<SiteTestimonialRow>('site_testimonials');
export const useDeleteSiteTestimonial = () => useContentDelete('site_testimonials');

export const useSitePosts = (siteId: string | undefined) => useContentList<SitePostRow>('site_posts', siteId, 'created_at');
export const useUpsertSitePost = () => useContentUpsert<SitePostRow>('site_posts');
export const useDeleteSitePost = () => useContentDelete('site_posts');

// ─────────────────────────────────────────────────────────────────────
// Inquiries + newsletter (studio side)
// ─────────────────────────────────────────────────────────────────────

export function useSiteInquiries(siteId: string | undefined) {
  return useQuery({
    queryKey: [SITES_V2_KEY, 'site_inquiries', siteId],
    enabled: !!siteId,
    queryFn: async (): Promise<SiteInquiryRow[]> => {
      const res = await db.from('site_inquiries').select('*').eq('site_id', siteId).order('created_at', { ascending: false }).limit(200);
      return rowsOf<SiteInquiryRow>(res);
    },
  });
}

export function useUpdateSiteInquiry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; site_id: string; values: Partial<Pick<SiteInquiryRow, 'status' | 'replied_at' | 'inquiry_id'>> }) => {
      const res = await db.from('site_inquiries').update(input.values).eq('id', input.id).select('*').single();
      return rowOf<SiteInquiryRow>(res);
    },
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: [SITES_V2_KEY, 'site_inquiries', row.site_id] });
    },
  });
}

export function useSiteInquiryMessages(inquiryId: string | undefined) {
  return useQuery({
    queryKey: [SITES_V2_KEY, 'site_inquiry_messages', inquiryId],
    enabled: !!inquiryId,
    queryFn: async (): Promise<SiteInquiryMessageRow[]> => {
      const res = await db.from('site_inquiry_messages').select('*').eq('inquiry_id', inquiryId).order('created_at', { ascending: true });
      return rowsOf<SiteInquiryMessageRow>(res);
    },
  });
}

export function useAddSiteInquiryMessage() {
  const qc = useQueryClient();
  const { data: org } = useOrganization();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (input: { inquiry: SiteInquiryRow; body: string; direction?: 'out' | 'note' }) => {
      if (!org?.id) throw new Error('Your studio isn’t ready yet.');
      const ins = await db
        .from('site_inquiry_messages')
        .insert({ org_id: org.id, inquiry_id: input.inquiry.id, direction: input.direction ?? 'out', body: input.body, created_by: user?.id ?? null })
        .select('*')
        .single();
      const msg = rowOf<SiteInquiryMessageRow>(ins);
      if ((input.direction ?? 'out') === 'out' && input.inquiry.status === 'new') {
        await db.from('site_inquiries').update({ status: 'replied', replied_at: new Date().toISOString() }).eq('id', input.inquiry.id);
      }
      return msg;
    },
    onSuccess: (msg, vars) => {
      qc.invalidateQueries({ queryKey: [SITES_V2_KEY, 'site_inquiry_messages', msg.inquiry_id] });
      qc.invalidateQueries({ queryKey: [SITES_V2_KEY, 'site_inquiries', vars.inquiry.site_id] });
    },
  });
}

export function useSiteNewsletterSubscribers(siteId: string | undefined) {
  return useQuery({
    queryKey: [SITES_V2_KEY, 'site_newsletter_subscribers', siteId],
    enabled: !!siteId,
    queryFn: async (): Promise<Array<{ id: string; email: string; created_at: string; source: string }>> => {
      const res = await db.from('site_newsletter_subscribers').select('id, email, created_at, source').eq('site_id', siteId).order('created_at', { ascending: false });
      return rowsOf(res);
    },
  });
}

export function useSiteAnalytics(siteId: string | undefined, days = 30) {
  return useQuery({
    queryKey: [SITES_V2_KEY, 'analytics', siteId, days],
    enabled: !!siteId,
    staleTime: 60_000,
    queryFn: async (): Promise<SiteAnalytics> => {
      const res = await db.rpc('site_v2_analytics', { p_site_id: siteId, p_days: days });
      if (res.error) throw new Error(res.error.message);
      return res.data as SiteAnalytics;
    },
  });
}

// ─────────────────────────────────────────────────────────────────────
// Media library (studio_site_media) — public-bucket uploads for v2
// ─────────────────────────────────────────────────────────────────────

export const SITE_V2_IMAGE_BUCKET = 'brand-assets';

async function resizeImage(file: File, maxDim = 2560): Promise<{ blob: Blob; width: number; height: number; mime: string }> {
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, maxDim / Math.max(bitmap.width, bitmap.height));
    const w = Math.max(1, Math.round(bitmap.width * scale));
    const h = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('canvas unavailable');
    ctx.drawImage(bitmap, 0, 0, w, h);
    const blob: Blob = await new Promise((resolve, reject) =>
      canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('encode failed'))), 'image/jpeg', 0.86),
    );
    bitmap.close?.();
    return { blob, width: w, height: h, mime: 'image/jpeg' };
  } catch {
    return { blob: file, width: 0, height: 0, mime: file.type || 'application/octet-stream' };
  }
}

/** Upload an image to the PUBLIC bucket (permanent URL — a published
 *  site must never depend on a signed URL) and catalogue it. */
export function useUploadSiteImageV2() {
  const qc = useQueryClient();
  const { data: org } = useOrganization();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (input: { site_id: string; file: File; alt_text?: string | null }): Promise<StudioSiteMedia> => {
      if (!org?.id) throw new Error('Your studio isn’t set up yet — the photo couldn’t be uploaded.');
      const { blob, width, height, mime } = await resizeImage(input.file);
      const ext = mime === 'image/jpeg' ? 'jpg' : mime.split('/')[1] || 'bin';
      const base = input.file.name.replace(/\.[^.]+$/, '').toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 60) || 'photo';
      const path = `${org.id}/sites/${input.site_id}/media/${Date.now()}-${base}.${ext}`;
      const up = await supabase.storage.from(SITE_V2_IMAGE_BUCKET).upload(path, blob, { contentType: mime, upsert: false, cacheControl: '31536000' });
      if (up.error) throw up.error;
      const { data: pub } = supabase.storage.from(SITE_V2_IMAGE_BUCKET).getPublicUrl(path);
      const ins = await db
        .from('studio_site_media')
        .insert({
          org_id: org.id,
          site_id: input.site_id,
          kind: 'image',
          storage_path: `${SITE_V2_IMAGE_BUCKET}:${path}`,
          public_url: pub.publicUrl,
          width: width || null,
          height: height || null,
          alt_text: input.alt_text ?? input.file.name.replace(/\.[^.]+$/, ''),
          created_by: user?.id ?? null,
          metadata: { original_name: input.file.name, original_type: input.file.type, original_size: input.file.size, bucket: SITE_V2_IMAGE_BUCKET, v2: true },
        })
        .select('*')
        .single();
      return rowOf<StudioSiteMedia>(ins);
    },
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: ['studio-site-media', row.site_id] });
    },
  });
}

export function useDeleteSiteMediaV2() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { media: StudioSiteMedia }) => {
      const m = input.media;
      if (m.storage_path?.startsWith(`${SITE_V2_IMAGE_BUCKET}:`)) {
        await supabase.storage.from(SITE_V2_IMAGE_BUCKET).remove([m.storage_path.slice(SITE_V2_IMAGE_BUCKET.length + 1)]);
      }
      const res = await db.from('studio_site_media').delete().eq('id', m.id);
      if (res.error) throw new Error(res.error.message);
      return m;
    },
    onSuccess: (m) => {
      qc.invalidateQueries({ queryKey: ['studio-site-media', m.site_id] });
      qc.invalidateQueries({ queryKey: [SITES_V2_KEY] });
    },
  });
}

export { useStudioSiteMedia as useSiteMediaV2 };

// ─────────────────────────────────────────────────────────────────────
// Draft snapshot (editor preview + Admin previews) — mirrors
// site_v2_compile_snapshot so the preview IS what publish will ship.
// ─────────────────────────────────────────────────────────────────────

export function compileDraftSnapshot(input: {
  site: StudioSiteV2;
  org?: { name?: string | null; logo_url?: string | null; brand_color?: string | null } | null;
  projects?: SiteProjectRow[];
  projectMedia?: SiteProjectMediaRow[];
  services?: SiteServiceRow[];
  testimonials?: SiteTestimonialRow[];
  posts?: SitePostRow[];
  media?: StudioSiteMedia[];
  /** Include drafts (Admin previews of unpublished projects). */
  includeDrafts?: boolean;
}): SiteSnapshot {
  const mediaById = new Map((input.media ?? []).map((m) => [m.id, m]));
  const ref = (id: string | null | undefined, caption?: string | null) => mediaRefFromRow(id ? mediaById.get(id) : null, caption);
  const byProject = new Map<string, SiteProjectMediaRow[]>();
  (input.projectMedia ?? []).forEach((pm) => {
    const list = byProject.get(pm.project_id) ?? [];
    list.push(pm);
    byProject.set(pm.project_id, list);
  });
  const now = Date.now();

  const projects: SiteProjectPublic[] = (input.projects ?? [])
    .filter((p) => input.includeDrafts || p.status === 'published')
    .map((p) => ({
      id: p.id,
      slug: p.slug,
      title: p.title,
      category: p.category,
      client: p.client,
      year: p.year,
      location: p.location,
      role: p.role,
      intro: p.intro,
      body: p.body,
      stats: Array.isArray(p.stats) ? p.stats : [],
      featured: !!p.featured,
      cover: ref(p.cover_media_id),
      media: (byProject.get(p.id) ?? [])
        .sort((a, b) => a.sort_order - b.sort_order)
        .map((pm) => ref(pm.media_id, pm.caption))
        .filter((m): m is SiteMediaRef => !!m),
    }));

  const services: SiteServicePublic[] = (input.services ?? [])
    .filter((s) => input.includeDrafts || s.visible)
    .map((s) => ({ id: s.id, name: s.name, price: s.price, duration: s.duration, includes: Array.isArray(s.includes) ? s.includes : [], blurb: s.blurb, featured: !!s.featured }));

  const testimonials: SiteTestimonialPublic[] = (input.testimonials ?? [])
    .filter((t) => input.includeDrafts || t.visible)
    .map((t) => ({ id: t.id, quote: t.quote, author: t.author, role: t.role }));

  const posts: SitePostPublic[] = (input.posts ?? [])
    .filter((p) => input.includeDrafts || p.status === 'published' || (p.status === 'scheduled' && p.published_at && new Date(p.published_at).getTime() <= now))
    .sort((a, b) => (b.published_at ?? b.created_at).localeCompare(a.published_at ?? a.created_at))
    .map((p) => ({ id: p.id, slug: p.slug, title: p.title, excerpt: p.excerpt, body: p.body, tags: p.tags ?? [], published_at: p.published_at, read_minutes: p.read_minutes, cover: ref(p.cover_media_id) }));

  return {
    version: 2,
    template: input.site.site_template,
    config: input.site.site_config ?? {},
    slug: input.site.slug,
    org: { name: input.org?.name ?? null, logo_url: input.org?.logo_url ?? null, brand_color: input.org?.brand_color ?? null },
    projects,
    services,
    testimonials,
    posts,
    captured_at: new Date().toISOString(),
  };
}

// ─────────────────────────────────────────────────────────────────────
// Public (anon) — what the live renderer calls
// ─────────────────────────────────────────────────────────────────────

export interface PublicSiteV2Result {
  found: boolean;
  live: boolean;
  site_id?: string;
  slug?: string;
  published_at?: string | null;
  snapshot: SiteSnapshot | null;
}

const parsePublic = (raw: unknown): PublicSiteV2Result => {
  const r = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  return {
    found: !!r.found,
    live: !!r.live,
    site_id: typeof r.site_id === 'string' ? r.site_id : undefined,
    slug: typeof r.slug === 'string' ? r.slug : undefined,
    published_at: typeof r.published_at === 'string' ? r.published_at : null,
    snapshot: parseSnapshot(r.snapshot),
  };
};

export function usePublicSiteV2(slug: string | undefined) {
  return useQuery({
    queryKey: ['public-site-v2', 'slug', slug],
    enabled: !!slug,
    staleTime: 60_000,
    queryFn: async (): Promise<PublicSiteV2Result> => {
      const res = await db.rpc('get_public_site_v2', { p_slug: slug });
      if (res.error) throw new Error(res.error.message);
      return parsePublic(res.data);
    },
  });
}

export function usePublicSiteV2ByHost(host: string | null | undefined) {
  return useQuery({
    queryKey: ['public-site-v2', 'host', host],
    enabled: !!host,
    staleTime: 60_000,
    queryFn: async (): Promise<PublicSiteV2Result> => {
      const res = await db.rpc('get_public_site_v2_by_host', { p_host: host });
      if (res.error) throw new Error(res.error.message);
      return parsePublic(res.data);
    },
  });
}

export async function submitSiteInquiry(slug: string, payload: Record<string, unknown>): Promise<void> {
  const res = await db.rpc('submit_site_inquiry', { p_slug: slug, p_payload: { ...payload, ...visitorMeta() } });
  if (res.error) throw new Error(friendlyRpcError(res.error.message));
}

export async function subscribeSiteNewsletter(slug: string, email: string): Promise<void> {
  const res = await db.rpc('subscribe_site_newsletter', { p_slug: slug, p_email: email });
  if (res.error) throw new Error(friendlyRpcError(res.error.message));
}

export function trackSiteEvent(slug: string, event: Record<string, unknown>): void {
  try {
    void db.rpc('track_site_event', { p_slug: slug, p_event: { ...event, ...visitorMeta(), referrer: typeof document !== 'undefined' ? document.referrer.slice(0, 500) : '' } }).then(() => undefined, () => undefined);
  } catch {
    /* beacons never throw */
  }
}

/** Postgres wraps our RAISE messages; keep the human sentence. */
const friendlyRpcError = (msg: string): string => msg.replace(/^.*?:\s*/, '').trim() || 'Something went wrong. Please try again.';

/** Random per-browser + per-tab ids for analytics. No fingerprinting. */
function visitorMeta(): { visitor_id: string; session_id: string; device: 'desktop' | 'mobile' | 'tablet'; source: string } {
  let visitor = '';
  let session = '';
  try {
    visitor = localStorage.getItem('orbit-site-visitor') ?? '';
    if (!visitor) {
      visitor = randomId();
      localStorage.setItem('orbit-site-visitor', visitor);
    }
    session = sessionStorage.getItem('orbit-site-session') ?? '';
    if (!session) {
      session = randomId();
      sessionStorage.setItem('orbit-site-session', session);
    }
  } catch {
    visitor = visitor || randomId();
    session = session || randomId();
  }
  const w = typeof window !== 'undefined' ? window.innerWidth : 1440;
  const device = w <= 760 ? 'mobile' : w <= 1100 ? 'tablet' : 'desktop';
  return { visitor_id: visitor, session_id: session, device, source: sourceFromReferrer() };
}

function randomId(): string {
  try {
    return crypto.randomUUID().replace(/-/g, '').slice(0, 24);
  } catch {
    return Math.random().toString(36).slice(2) + Date.now().toString(36);
  }
}

function sourceFromReferrer(): string {
  if (typeof document === 'undefined') return '';
  try {
    const url = new URL(window.location.href);
    const utm = url.searchParams.get('utm_source');
    if (utm) return utm.slice(0, 40);
    if (!document.referrer) return 'Direct';
    const host = new URL(document.referrer).hostname.replace(/^www\./, '');
    if (host === window.location.hostname.replace(/^www\./, '')) return '';
    if (/instagram\.com|l\.instagram/.test(host)) return 'Instagram';
    if (/google\./.test(host)) return 'Google';
    if (/pinterest\./.test(host)) return 'Pinterest';
    if (/facebook\.com|fb\.com|l\.facebook/.test(host)) return 'Facebook';
    if (/tiktok\.com/.test(host)) return 'TikTok';
    if (/t\.co|twitter\.com|x\.com/.test(host)) return 'X';
    if (/youtube\.com|youtu\.be/.test(host)) return 'YouTube';
    if (/vimeo\.com/.test(host)) return 'Vimeo';
    return host.slice(0, 40);
  } catch {
    return '';
  }
}
